// ============================================================================
// STATISTIQUES › VENDEURS › VENDEURS PAR PÉRIODE — qui vend quoi, semaine après semaine : une ligne par vendeur, une colonne
// par période, une mesure à la fois. Réf. maquette du back-office (« Vendeurs par période »).
//
// ⚠️ L'ÉCRAN NOMME ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : les colonnes (leurs jours lus), chaque mesure de chaque cellule
//    et la colonne Total viennent du serveur (`sales/stats-sellers-periods.ts`). Changer de mesure ne relit rien : la
//    réponse porte les cinq.
// ⚠️ LES MESURES SONT CELLES DE LA VENTE : commandes, livraisons et entrées de la maquette n'ont pas de vendeur.
// ⚠️ UNE CELLULE SANS VENTE SE LIT « — », jamais « 0 € » ; une cellule dont les ventes s'annulent (une vente et son retour)
//    se lit 0. Un taux non mesurable aussi « — ».
// ============================================================================
import { useState, type ReactNode } from 'react';
import { Icon, Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { SellerPeriodCell, SellerPeriodsView, StockPeriodGrain } from '../api';
import { key, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { exporterCsv } from './export-csv';
import { POP_HEAD, StatsPopover } from './stats-bar';
import { coef, csvCoef, csvQty, csvRate, eur0, pc, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, type GridCell, type GridColumn, type StatsSort } from './stats-grid';
import { PeriodGrainPicker, bucketNames } from './stats-stock-periods';

/** Une mesure qu'on éclate dans le temps. */
export type SellerPeriodMeasure = 'netTtcCents' | 'marginHtCents' | 'qtySold' | 'marginRateBp' | 'coefBp';

type Unit = 'money' | 'qty' | 'rate' | 'coef';
interface Measure { k: SellerPeriodMeasure; label: Translatable; desc: Translatable; unit: Unit; color: string }

/** Les mesures, dans l'ordre du dessin — sans celles des achats, qui n'ont pas de vendeur. */
export const SELLER_PERIOD_MEASURES: Measure[] = [
  { k: 'netTtcCents', label: key('CA net TTC'), desc: key('Remises déduites'), unit: 'money', color: 'var(--teal-500)' },
  { k: 'marginHtCents', label: key('Marge {symbole} HT'), desc: key('CA net HT − {achat} HT'), unit: 'money', color: 'var(--violet)' },
  { k: 'qtySold', label: key('Quantités vendues'), desc: key('Pièces vendues en caisse'), unit: 'qty', color: 'var(--ink-700)' },
  { k: 'marginRateBp', label: key('Marge %'), desc: key('Taux — recalculé sur les sommes'), unit: 'rate', color: 'var(--violet)' },
  { k: 'coefBp', label: key('Coef'), desc: key('PV TTC / {achat} HT — recalculé sur les sommes'), unit: 'coef', color: 'var(--amber)' },
];

export const DEFAULT_SELLER_PERIOD_MEASURE: SellerPeriodMeasure = 'netTtcCents';

/** Le tri d'ouverture : l'ordre des vendeurs, à égalité le plus gros Total d'abord. */
export const SELLER_PERIODS_DEFAULT_SORT: StatsSort = { k: 'all', d: -1, man: false };

const ALL_KEY = 'all';

const measureOf = (k: SellerPeriodMeasure): Measure => SELLER_PERIOD_MEASURES.find((m) => m.k === k) ?? SELLER_PERIOD_MEASURES[0]!;
/** Le nom d'une mesure et sa ligne d'aide — la devise et le nom du prix d'achat sont ceux du magasin. */
const measureLabel = (m: Measure): string => t(m.label, { symbole: currentCurrency().symbol });
const measureDesc = (m: Measure): string => t(m.desc, { achat: fieldLabel('purchasePriceExclTax') });

// ─── Les réglages de l'onglet ────────────────────────────────────────────────────────────────────

/** Au bout de la ligne PÉRIODE : la mesure éclatée (un menu), puis « Éclater par » Semaine · Mois · Année. */
export function SellerPeriodsControls({ measure, onMeasure, grain, onGrain }: {
  measure: SellerPeriodMeasure;
  onMeasure: (m: SellerPeriodMeasure) => void;
  grain: StockPeriodGrain;
  onGrain: (g: StockPeriodGrain) => void;
}) {
  const [menu, setMenu] = useState<DOMRect | null>(null);
  const m = measureOf(measure);
  return (
    <>
      <button type="button" aria-expanded={!!menu} title={t('Changer la mesure éclatée dans le temps')}
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu((x) => (x ? null : r)); }}
        className="oz-stats-measure"
        style={{
          height: 30, padding: '0 11px', border: `1.5px solid ${m.color}`, borderRadius: 'var(--r-pill)', background: 'var(--paper)',
          color: m.color, fontSize: 'var(--t-dense)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 7,
          whiteSpace: 'nowrap', cursor: 'pointer', boxSizing: 'border-box',
        }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: m.color, flexShrink: 0 }} />
        {measureLabel(m)}
        <Icon name="chevronD" size={12} stroke={2.4} />
      </button>
      {menu && (
        <StatsPopover r={menu} width={286} onClose={() => setMenu(null)}>
          <div style={POP_HEAD}>{t('Mesure éclatée')}</div>
          <div role="group" aria-label={t('Mesure éclatée')} style={{ padding: '0 5px 5px' }}>
            {SELLER_PERIOD_MEASURES.map((x) => {
              const on = x.k === measure;
              return (
                <button key={x.k} type="button" aria-pressed={on} onClick={() => { onMeasure(x.k); setMenu(null); }}
                  className="oz-stats-measure-pick"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px', border: 'none', borderRadius: 'var(--r-xs)',
                    background: on ? 'var(--ink-50)' : 'transparent', textAlign: 'left', cursor: 'pointer',
                  }}>
                  <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: x.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: on ? 'var(--ink-900)' : 'var(--ink-800)' }}>{measureLabel(x)}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>{measureDesc(x)}</span>
                  </span>
                  {on && <Icon name="check" size={13} stroke={3} style={{ color: 'var(--teal-600)', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </StatsPopover>
      )}
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--ink-200)', margin: '0 2px' }} />
      <PeriodGrainPicker grain={grain} onGrain={onGrain} />
    </>
  );
}

// ─── Le tri et les cellules ──────────────────────────────────────────────────────────────────────

type Row = SellerPeriodsView['rows'][number];

/** La cellule d'une ligne dans une colonne (une période, ou le Total). */
const cellAt = (r: { cells: SellerPeriodCell[]; all: SellerPeriodCell }, k: string, view: SellerPeriodsView): SellerPeriodCell | null =>
  (k === ALL_KEY ? r.all : r.cells[view.buckets.findIndex((b) => b.key === k)] ?? null);

/** La valeur d'une cellule pour la mesure — `null` sans vente ou quand elle ne se mesure pas. */
const valueOf = (c: SellerPeriodCell | null, m: SellerPeriodMeasure): number | null => (c === null || c.lines === 0 ? null : c[m]);

/** Les vendeurs dans l'ordre demandé — par leur NOM (leur valeur est l'identifiant de l'opérateur), à égalité le plus gros Total. */
export function sortSellerPeriodRows(view: SellerPeriodsView, measure: SellerPeriodMeasure, sort: StatsSort, lang: string): Row[] {
  return sortStatsRows(view.rows, 1, sort, (r, k) => valueOf(cellAt(r, k, view), measure),
    (x, y) => (valueOf(y.all, measure) ?? -Infinity) - (valueOf(x.all, measure) ?? -Infinity), lang, (p) => p.label ?? p.value);
}

/** Une valeur mise en forme selon son unité. */
function formatValue(v: number | null, unit: Unit): string {
  if (unit === 'money') return eur0(v);
  if (unit === 'rate') return pc(v);
  if (unit === 'coef') return coef(v);
  return qty(v);
}

/** Une cellule : la valeur de la mesure, « — » sans vente ou non mesurable. */
function cell(c: SellerPeriodCell, m: Measure, strong: boolean): GridCell {
  const v = valueOf(c, m.k);
  return { text: formatValue(v, m.unit), fg: v === null ? 'var(--ink-300)' : strong ? 'var(--ink-900)' : 'var(--ink-800)', fw: strong ? 800 : 600 };
}

/** `AAAAMMJJ` → « JJ/MM/AAAA » dans la langue du compte. */
const dayLabel = (ymd: string, lang: string): string =>
  new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(4, 6) - 1, +ymd.slice(6, 8), 12)));

/** Les colonnes : une par période — ses jours lus en infobulle —, puis le Total. */
function columnsOf(view: SellerPeriodsView, m: Measure, lang: string): GridColumn[] {
  return [
    ...view.buckets.map((b): GridColumn => {
      const names = bucketNames(b, view.grain, lang);
      return {
        k: b.key, label: names.label, sub: names.sub, width: 92, track: 'minmax(92px,1fr)', color: m.color, headFg: 'var(--ink-800)',
        title: b.partial
          ? t('Du {du} au {au} — la période choisie ne couvre qu’une partie de cette colonne', { du: dayLabel(b.from, lang), au: dayLabel(b.to, lang) })
          : t('Du {du} au {au}', { du: dayLabel(b.from, lang), au: dayLabel(b.to, lang) }),
      };
    }),
    {
      k: ALL_KEY, label: t('Total'), sub: t('périodes affichées'), width: 124, track: '124px', color: 'var(--ink-700)', headBg: 'var(--ink-50)',
      headFg: 'var(--ink-900)', title: t('Trier sur le total'),
    },
  ];
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/** LE TABLEAU DE VENDEURS PAR PÉRIODE — une colonne par période et le Total, dans le squelette commun. */
export function SellerPeriodsTable({ view, measure, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: SellerPeriodsView;
  measure: SellerPeriodMeasure;
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const m = measureOf(measure);
  const rows = sortSellerPeriodRows(view, measure, sort, lang);
  const totalSub = [t('{n} vendeur', { n: view.rows.length }), ...(sort.man ? [] : [t('ordre des vendeurs')])].join(' · ');
  const strong = (x: ReactNode) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>;

  return (
    <StatsGrid label={t('Vendeurs par période')} sort={sort} onSort={onSort} busy={busy} lastAxis={null}
      axes={[{ label: t('Vendeur') }]}
      columns={columnsOf(view, m, lang)}
      total={{ sub: totalSub, cells: [...view.total.cells.map((c) => cell(c, m, true)), cell(view.total.all, m, true)] }}
      rows={rows.map((r) => ({
        key: r.parts[0]!.value, parts: [{ value: r.parts[0]!.label, label: r.parts[0]!.label }], sub: t('{n} article', { n: r.articles }),
        cells: [...r.cells.map((c) => cell(c, m, false)), cell(r.all, m, true)],
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune vente sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre période.')}</div>
                </>
              )}
            </div>
          )}
          {view.truncated && (
            // ⚠️ LA COUPURE SE DIT : le Total ne compte que les périodes affichées.
            <div role="note" style={{
              padding: '9px 16px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 600,
              borderTop: '1px solid var(--ink-200)',
            }}>
              {t('{n} seule période est montrée, et le Total ne compte qu’elles : choisissez une période plus courte ou un grain plus large.', { n: view.buckets.length })}
            </div>
          )}
          <div style={{
            padding: '10px 16px', background: 'var(--ink-50)', borderRadius: '0 0 var(--r-sm) var(--r-sm)', fontSize: 'var(--t-meta)',
            color: 'var(--ink-500)', lineHeight: 1.55,
          }}>
            <Trans k='Une seule mesure à la fois — ici {mesure}. Les montants et les quantités sont <1>additifs</1> : le TOTAL est la somme exacte des périodes affichées. La marge % et le coef sont des <2>taux</2> — ils se recalculent sur les sommes, jamais moyennés. Une période que la période choisie ne couvre qu’en partie ne compte que ses jours dedans ; une cellule sans vente se lit « — ».'
              vars={{ mesure: measureLabel(m) }} parts={[strong, strong]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : la mesure choisie, une colonne par période puis le Total, le tri, la ligne de total.
 * ⚠️ UNE CELLULE SANS VENTE OU NON MESURABLE RESTE VIDE — un 0 dans un tableur devient une somme fausse.
 */
export function exportSellerPeriods(view: SellerPeriodsView, measure: SellerPeriodMeasure, sort: StatsSort, lang: string): void {
  const m = measureOf(measure);
  const unit = m.unit === 'money' ? ` (${currentCurrency().code})` : m.unit === 'rate' ? ' (%)' : '';
  const value = (c: SellerPeriodCell): string => {
    const v = valueOf(c, measure);
    if (v === null) return '';
    if (m.unit === 'money') return toInputValue(v);
    if (m.unit === 'rate') return csvRate(v);
    if (m.unit === 'coef') return csvCoef(v);
    return csvQty(v);
  };
  const heads = [
    ...view.buckets.map((b) => { const n = bucketNames(b, view.grain, lang); return `${n.label} ${n.sub}${unit}`; }),
    `${t('Total')}${unit}`,
  ];
  exporterCsv(`vendeurs-par-periode-${measure}-${view.grain}-${view.period.from}-${view.period.to}`,
    [t('Vendeur'), t('Articles'), ...heads],
    [
      [t('TOTAL'), String(view.scope.references), ...[...view.total.cells, view.total.all].map(value)],
      ...sortSellerPeriodRows(view, measure, sort, lang).map((r) => [
        shownValue(r.parts[0]!.label), String(r.articles), ...[...r.cells, r.all].map(value),
      ]),
    ]);
}
